/**
 * The DM COMPOSER-CONTEXT REGISTRY — which `context` values mean "this editor is
 * composing a direct message" (network-roster-messaging D1(a)).
 *
 * WHY IT EXISTS. The We composer stack takes an opaque `context` prop that every
 * branch point used to compare against ONE host's string literal,
 * `'channelMemberDetail'`. That literal was load-bearing in four independent
 * places — the `UserDM_<actorId>` draft key in `weMessageDrafts`, the DM dispatch
 * in `weMessageSending.sendMessage`, the chrome paring in
 * `ExpandedMessageInput`/`MobileMessageInput`, and the "Message <name>"
 * placeholder — so the SECOND host of the same stack (the glance roster's member
 * popover) had exactly two options: impersonate the We host's literal, or fork
 * the stack. Both are wrong. Impersonation entangles two hosts on a string that
 * names neither of them; a fork duplicates the one v2 editor.
 *
 * So DM-ness stops being a string comparison and becomes MEMBERSHIP in a declared
 * set. A third host is a registry entry here and nothing else — no branch point
 * anywhere may compare a context against a host string literal again.
 *
 * WHAT IS *NOT* IN SCOPE. This registry answers "is this composer a DM composer?"
 * and only that. It does NOT identify a host: two hosts share every DM behavior
 * (draft key, dispatch, pared chrome, non-anonymous drafts) precisely because
 * those behaviors follow the DM POSTURE, not the surface. Anything a single host
 * needs alone — the glance popover's teleport, its explicit send button, its
 * dismiss-on-hand-off — belongs to that host's component, never to a branch on
 * the context value.
 *
 * THE PREDICATE IS TYPE-SAFE ON PURPOSE. `context` is polymorphic across the
 * stack: a plain string for the DM hosts, an object carrying `.scenario` for
 * thread creation / Artemis responses / spotlight views, and `null` or
 * `undefined` for the ordinary routed-topic composer. `isDmComposerContext`
 * therefore narrows to a string before testing membership, so every non-DM shape
 * answers `false` without the caller pre-checking.
 */

/** The We tab's hub member detail popover (`ChannelMemberDetail.vue`). */
export const CHANNEL_MEMBER_DETAIL_CONTEXT = "channelMemberDetail";

/** The network glance roster's member popover. */
export const GLANCE_MEMBER_POPOVER_CONTEXT = "glanceMemberPopover";

/**
 * Every registered DM composer context, frozen so a consumer cannot widen the
 * registry from the outside — registration is a source edit here, by design.
 */
export const DM_COMPOSER_CONTEXTS = Object.freeze([
  CHANNEL_MEMBER_DETAIL_CONTEXT,
  GLANCE_MEMBER_POPOVER_CONTEXT,
]);

const REGISTERED_DM_CONTEXTS = new Set(DM_COMPOSER_CONTEXTS);

/**
 * The ONE read path. `true` when `context` is a registered DM composer context.
 *
 * @param {unknown} context - the composer `context` prop, in any of its shapes.
 * @returns {boolean}
 */
export function isDmComposerContext(context) {
  return typeof context === "string" && REGISTERED_DM_CONTEXTS.has(context);
}
